import { type ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import {
  getConditionalRules,
  getRowGroups,
  isFullWidthField,
  matchesConditionalShowIf,
  type JsonFormObjectSchema,
  type JsonFormSchema,
} from "containers/admin/clients/client/components/form-builder/form-schema";

const ROW_GROUP_GRID_COLS: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

export const renderGroupedPropertyEntries = (
  objectSchema: JsonFormObjectSchema,
  prefix: string,
  entries: [string, JsonFormSchema][],
  renderEntry: (entry: [string, JsonFormSchema]) => ReactNode,
  /**
   * Resolves a field's current value by name, scoped however the caller's entries are scoped (a
   * dynamic-section item's row, or the tab's own form data). Only needed to evaluate `x-full-width`
   * breakouts below — omitting it disables the breakout and every entry renders inline.
   */
  resolveFieldValue?: (fieldName: string) => unknown,
): ReactNode[] => {
  const rowGroups = getRowGroups(objectSchema);
  const entryNames = new Set(entries.map(([name]) => name));
  const renderedFieldNames = new Set<string>();

  const fullWidthRules = resolveFieldValue
    ? getConditionalRules(objectSchema).filter((rule) =>
        rule.dependentFieldNames.some((dependentName) =>
          isFullWidthField(objectSchema.properties[dependentName]),
        ),
      )
    : [];

  // A trigger field flagged `x-full-width` on one of its dependents renders that dependent as its own
  // row directly below the entry/row-group holding the trigger, instead of nested inside the trigger's
  // own grid cell — see `x-full-width` in `form-schema.ts`.
  const renderFullWidthDependents = (renderedNames: string[]): ReactNode[] =>
    fullWidthRules.flatMap((rule) => {
      if (!renderedNames.includes(rule.triggerFieldName) || !resolveFieldValue) {
        return [];
      }

      const isShown = matchesConditionalShowIf(
        resolveFieldValue(rule.triggerFieldName),
        rule.showIf,
      );

      if (!isShown) {
        return [];
      }

      return rule.dependentFieldNames.flatMap((dependentName): ReactNode[] => {
        const dependentField = objectSchema.properties[dependentName];

        return dependentField && isFullWidthField(dependentField)
          ? [renderEntry([dependentName, dependentField])]
          : [];
      });
    });

  return entries.flatMap(([name, field]): ReactNode[] => {
    if (renderedFieldNames.has(name)) {
      return [];
    }

    const rowGroup = rowGroups.find(
      (group) =>
        group[0] === name && group.every((propertyName) => entryNames.has(propertyName)),
    );

    if (rowGroup) {
      rowGroup.forEach((propertyName) => renderedFieldNames.add(propertyName));

      // A cell that renders nothing takes no column — the end of an `x-date-ranges` pair, which its
      // start draws as one range picker across the row.
      const cells = rowGroup
        .flatMap((propertyName): ReactNode[] => {
          const rowField = objectSchema.properties[propertyName];

          return rowField ? [renderEntry([propertyName, rowField])] : [];
        })
        .filter((cell) => cell !== null);

      return [
        <div
          key={`${prefix || "root"}-row-${rowGroup.join("-")}`}
          className={twMerge("grid w-full gap-3", ROW_GROUP_GRID_COLS[cells.length] ?? "grid-cols-1")}
        >
          {cells}
        </div>,
        ...renderFullWidthDependents(rowGroup),
      ];
    }

    renderedFieldNames.add(name);

    return [renderEntry([name, field]), ...renderFullWidthDependents([name])];
  });
};
